import {
  Box,
  CloseButton,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  Image,
} from '@chakra-ui/react';
import { FiActivity } from 'react-icons/fi';
import { FinalItem } from './FinalItem';
import { NavItemSup } from './NavItemSup';
import { PwaInstallButton } from './PwaInstallButton';
import { HowToInstallAppModal } from './HowToInstallAppModal';
import Bowser from "bowser"; // 🧭 Para detectar navegador

/**
 * SidebarContent renders the left navigation drawer with Clinical Vitality styling:
 *   • Modern dark slate theme (#0f172a) matching Google Stitch design
 *   • Top branded identity container (Medical mark + Angels on Watch + 24/7 subtitle)
 *   • Scrollable navigation zone with active pill indicators
 *   • Dynamic PWA install/open prompt
 *   • Bottom user profile card with direct quick-actions
 */
export const SidebarContent = ({ onClose, ...rest }: any) => {
  // 🧭 Detección de navegador
  let isOpera = false;
  let isSafari = false;
  if (typeof window !== "undefined") {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const browserName = browser.getBrowserName() || "";
    const lowerBrowser = browserName.toLowerCase();
    isOpera = lowerBrowser.includes("opera") || window.navigator.userAgent.includes("OPR/");
    isSafari =
      (lowerBrowser.includes("safari") && !lowerBrowser.includes("chrome")) ||
      /Mobile\/.*Safari/.test(window.navigator.userAgent);
  }

  return (
    <Box
      transition="0.2s ease"
      bg="#0f172a"
      color="white"
      borderRight="1px solid"
      borderRightColor="whiteAlpha.100"
      w={{ base: 'xs', xl: '256px' }}
      pos="fixed"
      h="full"
      zIndex="sticky"
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.25)"
      {...rest}
    >
      <Flex
        flexDirection="column"
        h="full"
        w="full"
        overflow="hidden"
      >
        {/* Top Brand Identity Container */}
        <HStack
          h="64px"
          px={4}
          justify="space-between"
          align="center"
          borderBottom="1px solid"
          borderBottomColor="whiteAlpha.100"
          flexShrink={0}
        >
          <HStack spacing={3} align="center">
            <Box
              w="38px"
              h="38px"
              borderRadius="xl"
              bg="teal.500"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="white"
              boxShadow="0 2px 8px rgba(13, 148, 136, 0.4)"
              flexShrink={0}
            >
              <Icon as={FiActivity} boxSize={5} />
            </Box>
            <VStack align="flex-start" spacing={0}>
              <Text
                fontSize="15px"
                fontWeight="bold"
                color="white"
                lineHeight="1.2"
                letterSpacing="-0.02em"
              >
                Angels on Watch
              </Text>
              <Text
                fontSize="11px"
                fontWeight="semibold"
                color="teal.300"
                fontFamily="mono"
              >
                Teleasistencia 24/7
              </Text>
            </VStack>
          </HStack>

          <CloseButton
            display={{ base: 'flex', xl: 'none' }}
            onClick={onClose}
            color="whiteAlpha.800"
            _hover={{ bg: 'whiteAlpha.200' }}
          />
        </HStack>

        {/* Scrollable Navigation & Controls Zone */}
        <Box
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          py={4}
          px={3}
          css={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
          }}
        >
          <NavItemSup onClose={onClose} />

          {/* Dynamic PWA installation prompt */}
          <Box mt={4} mb={2} px={1}>
            {isOpera || isSafari ? <HowToInstallAppModal /> : <PwaInstallButton />}
          </Box>
        </Box>

        {/* Footer with user session & profile actions */}
        <Box
          p={3}
          borderTop="1px solid"
          borderTopColor="whiteAlpha.100"
          flexShrink={0}
          bg="#0c1322"
        >
          <FinalItem />
        </Box>
      </Flex>
    </Box>
  );
};
